/**
 * ============================================================================
 * Calculator Design System
 * ============================================================================
 * 
 * Couleurs, icônes et configuration visuelle pour le Calculator
 * 
 * ============================================================================
 */

import {
  Sprout,
  Zap,
  TrendingUp,
  Truck,
  Trash2,
  Flame,
  Cloud,
  Wifi,
  Building2,
  Car,
  ShoppingCart,
  Droplets,
  type LucideIcon,
} from 'lucide-react';

export interface CategoryDesign {
  color: string;
  bgColor: string;
  icon: LucideIcon;
  gradient: string;
}

/**
 * Design system par catégorie d'émissions
 * Couleurs inspirées de Linear + Notion
 */
export const categoryDesigns: Record<string, CategoryDesign> = {
  'AGRICULTURE, FORESTRY & LAND USE': {
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
    icon: Sprout,
    gradient: 'from-emerald-500 to-green-600',
  },
  'ENERGY': {
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
    icon: Zap,
    gradient: 'from-amber-500 to-orange-600',
  },
  'CARBON ECONOMY': {
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    icon: TrendingUp,
    gradient: 'from-blue-500 to-indigo-600',
  },
  'DISTRIBUTION': {
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    icon: Truck,
    gradient: 'from-purple-500 to-violet-600',
  },
  'END-OF-LIFE TREATMENT': {
    color: 'text-slate-600',
    bgColor: 'bg-slate-50',
    icon: Trash2,
    gradient: 'from-slate-500 to-gray-600',
  },
  'FUGITIVE EMISSIONS': {
    color: 'text-red-600',
    bgColor: 'bg-red-50',
    icon: Flame,
    gradient: 'from-red-500 to-rose-600',
  },
  'CLIMATE FINANCE & POLICY': {
    color: 'text-cyan-600',
    bgColor: 'bg-cyan-50',
    icon: TrendingUp,
    gradient: 'from-cyan-500 to-teal-600',
  },
  'ICT / DIGITAL TRANSFORMATION': {
    color: 'text-indigo-600',
    bgColor: 'bg-indigo-50',
    icon: Wifi,
    gradient: 'from-indigo-500 to-blue-600',
  },
  'OTHER INDIRECT EMISSIONS': {
    color: 'text-gray-600',
    bgColor: 'bg-gray-50',
    icon: Cloud,
    gradient: 'from-gray-500 to-slate-600',
  },
  'PERSONAL TRANSPORT': {
    color: 'text-orange-600',
    bgColor: 'bg-orange-50',
    icon: Car,
    gradient: 'from-orange-500 to-amber-600',
  },
  'PURCHASES': {
    color: 'text-pink-600',
    bgColor: 'bg-pink-50',
    icon: ShoppingCart,
    gradient: 'from-pink-500 to-rose-600',
  },
  'WASTE': {
    color: 'text-yellow-600',
    bgColor: 'bg-yellow-50',
    icon: Trash2,
    gradient: 'from-yellow-500 to-amber-600',
  },
  'WATER USE': {
    color: 'text-sky-600',
    bgColor: 'bg-sky-50',
    icon: Droplets,
    gradient: 'from-sky-500 to-blue-600',
  },
};

/**
 * Obtenir le design d'une catégorie (avec fallback)
 */
export function getCategoryDesign(category: string): CategoryDesign {
  return categoryDesigns[category] || {
    color: 'text-gray-600',
    bgColor: 'bg-gray-50',
    icon: Building2,
    gradient: 'from-gray-500 to-slate-600',
  };
}

/**
 * Formater les émissions CO₂ avec unité appropriée
 */
export function formatCO2(tco2e: number): { value: string; unit: string } {
  if (tco2e >= 1000) {
    return {
      value: (tco2e / 1000).toFixed(2),
      unit: 'kt CO₂e',
    };
  } else if (tco2e >= 1) {
    return {
      value: tco2e.toFixed(2),
      unit: 't CO₂e',
    };
  } else {
    return {
      value: (tco2e * 1000).toFixed(0),
      unit: 'kg CO₂e',
    };
  }
}

/**
 * Calculer le pourcentage de progression
 */
export function calculateProgress(completed: number, total: number): number {
  if (total === 0) return 0;
  return Math.round((completed / total) * 100);
}

